import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '../../environments/environment';
import { LegacyPortalHandoffService } from '../../app/shared/services/legacy-portal-handoff.service';
import { LogoutService } from '../../app/shared/services/logout.service';

/** Action dispatched when a clickable menu row is chosen. A navigation row (one
    carrying `route`) needs only a new id here plus an {@link ProfileMenuItem}
    entry — the `default` branch of {@link UserProfileMenuComponent.onSelect}
    routes it. An imperative action (e.g. `logout`, `changePassword`) also needs a
    matching `switch` case. */
export type ProfileMenuAction = 'logout' | 'changePassword' | 'changeSecurityQuestions';

/**
 * A single clickable row in the profile dropdown. `route` is the target for
 * navigation actions; omit it for actions handled imperatively (e.g. `logout`,
 * `changePassword` — the latter is environment-gated via
 * {@link LegacyPortalHandoffService}, so it cannot be a plain in-app route).
 */
export interface ProfileMenuItem {
  readonly text: string;
  readonly id: ProfileMenuAction;
  readonly route?: string;
}

/**
 * Header user-profile dropdown: a circular avatar trigger that opens a menu of
 * account actions (Change Password, Change Security Questions, Logout; see
 * {@link items}) with a disabled version row pinned to the bottom.
 *
 * Design intent:
 * - **Data-driven** — rows come from the typed {@link items} array and every
 *   click funnels through {@link onSelect}, dispatched by `id`. A navigation
 *   row (one carrying `route`) needs only an array entry — the `default`
 *   branch of `onSelect` already routes it; an imperative action like
 *   `logout` or `changePassword` needs one array entry + one `switch` case.
 * - **Presentational trigger** — the avatar/name are `input()`s resolved by the
 *   parent (`TopBarComponentComponent` owns the avatar network/cache lifecycle);
 *   this component owns only the open/close + a11y behaviour.
 * - **Accessible** — WAI-ARIA menu-button semantics: `aria-haspopup`/`-expanded`
 *   on the trigger, `role="menu"`/`menuitem`, roving `tabindex`, arrow/Home/End
 *   navigation, a Tab focus-trap within the rows, and Escape/outside-click close
 *   that restores focus to the trigger.
 */
@Component({
  selector: 'app-user-profile-menu',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './user-profile-menu.component.html',
  styleUrl: './user-profile-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileMenuComponent {
  /** Routes menu items that navigate (those carrying a `route`). */
  private readonly router = inject(Router);
  /** Performs the app logout when the Logout item is selected. */
  private readonly logoutService = inject(LogoutService);
  /** Hands Change Password / Change Security Questions off to the legacy portal in production. */
  private readonly portalHandoff = inject(LegacyPortalHandoffService);
  /** Host element — used for outside-click detection and focus management. */
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  /** Injector for creating the outside-click `effect` in the right context. */
  private readonly injector = inject(Injector);

  /** Fallback avatar asset (served from `public/`) shown when the image fails. */
  private readonly DEFAULT_PROFILE = 'vectors/ProfileIcon.svg';

  /** Display name shown beside the avatar. */
  readonly userName = input<string>('');

  /** Resolved avatar `src` (data URI or asset path) supplied by the parent. */
  readonly avatarUrl = input<string>(this.DEFAULT_PROFILE);

  /** Whether the dropdown panel is currently open. */
  readonly isOpen = signal(false);

  /** Index of the roving-focus row while the menu is open. */
  readonly activeIndex = signal(0);

  /** Menu rows, top-to-bottom. `text` is an i18n key resolved via `| translate`
      in the template (order here is the rendered/navigation order). A static
      array: every row is available on every page. (The dashboard Refresh row
      that briefly lived here — gated on a live dashboard view — moved to the
      breadcrumb bar, 2026-08-06.) */
  readonly items: readonly ProfileMenuItem[] = [
    { text: 'profile.changePassword', id: 'changePassword' },
    { text: 'profile.changeSecurityQuestions', id: 'changeSecurityQuestions' },
    { text: 'profile.logout', id: 'logout' },
  ];

  /** App version label pulled from the environment config, rendered in the muted footer row. */
  readonly version = environment.PRODUCT_VERSION;

  /** Live references to the rendered menuitem buttons, for imperative focus. */
  private readonly menuItems = viewChildren<ElementRef<HTMLButtonElement>>('menuItem');

  /** Toggles the panel; opening focuses the first row for keyboard users. */
  toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  /** Opens the panel and moves focus to the first row after it renders. */
  open(): void {
    this.activeIndex.set(0);
    this.isOpen.set(true);
    // The rows only exist after the next render; afterNextRender is browser-only
    // so this safely no-ops during SSR.
    afterNextRender(
      () => this.menuItems()[0]?.nativeElement.focus(),
      { injector: this.injector },
    );
  }

  /** Closes the panel and returns focus to the trigger. */
  close(restoreFocus = true): void {
    if (!this.isOpen()) {
      return;
    }
    this.isOpen.set(false);
    if (restoreFocus) {
      this.host.nativeElement.querySelector<HTMLButtonElement>('.user')?.focus();
    }
  }

  /**
   * Dispatches a chosen row by `id`, then closes the menu. Navigation actions
   * route to their `route`; `logout` delegates to {@link LogoutService}.
   * @param item The selected menu row.
   */
  onSelect(item: ProfileMenuItem): void {
    this.close(false);
    switch (item.id) {
      case 'logout':
        this.logoutService.logout();
        break;
      case 'changePassword':
        this.portalHandoff.changePassword();
        break;
      case 'changeSecurityQuestions':
        this.portalHandoff.changeSecurityQuestions();
        break;
      default:
        if (item.route) {
          this.router.navigateByUrl(item.route);
        }
    }
  }

  /** Keyboard handling for the trigger button (ArrowDown/Up opens into the menu). */
  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
    }
  }

  /**
   * Keyboard handling inside the open menu: roving arrow navigation, Home/End,
   * a Tab focus-trap that cycles the rows, and Escape to close.
   * @param event The keydown event from the menu container.
   */
  onMenuKeydown(event: KeyboardEvent): void {
    const last = this.items.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(this.activeIndex() + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(this.activeIndex() - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        this.moveFocus(last);
        break;
      case 'Tab':
        // Trap focus within the rows so it never escapes the open menu.
        event.preventDefault();
        this.moveFocus(this.activeIndex() + (event.shiftKey ? -1 : 1));
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  /** Swaps the avatar `<img>` to the default asset when the image fails to load. */
  onImgError(event: Event): void {
    (event.target as HTMLImageElement).src = this.DEFAULT_PROFILE;
  }

  /** Closes the menu when a click lands outside this component. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  /** Wraps `index` into range and moves roving focus to that row. */
  private moveFocus(index: number): void {
    const items = this.menuItems();
    if (!items.length) {
      return;
    }
    const next = (index + items.length) % items.length;
    this.activeIndex.set(next);
    items[next]?.nativeElement.focus();
  }
}
